/* eslint-disable react/jsx-key */
import FavoriteIcon from '@mui/icons-material/Favorite';
import { styled, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { NextImage, RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setProfileData } from '../../../src/features/crProfileSlice';
import { hideLoader, showLoader } from '../../../src/features/loaderSlice';
import useSavedTrialsNearestSite from '../../lib/hooks/coveo/useSavedTrialsNearestSite';
import {
  fetchProfileDetailsForCR,
  updateSavedTrialsForMyProfile,
  updateSavedTrialsofCRRegUser,
} from '../../lib/utils/axiosCDPFetcher';
import { highlightExcerpt } from '../../lib/utils/coveoHeadless';
import { JSSLink } from '../shared/Links/JSSLink.styles';
import CRModal from './CRModal';
import { buildInteractiveResult, Result } from '@coveo/headless';
import headlessEngine from '../../lib/utils/coveo/headlessEngine';
import { mergeTag } from '../../lib/utils/common';
import getAgeRange from '../../lib/utils/coveo/getAgeRange';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import Tag from '../shared/Tags/Tag';
import { getStatusTags } from '../../lib/utils/coveo/fetchTrialStatusTags';
import { useI18n } from 'next-localization';
import { ITagProps } from './Search.type';
import UiText from '../shared/Typography/UiText';
import { DEFAULT_PRIMARY_FONT_FAMILY } from 'theme/fontDefaults';

const KeyMapper: any = {
  Age: 'trialminage',
  Phase: 'trialphase',
  'Participants needed': 'trialenrollment',
  'Est. Completion Date': 'trialcompletiondate',
};

export const StDescription = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'limit' && prop !== 'lineHeight',
})<any>(({ theme }) => ({
  lineHeight: `1.95ch`,
  color: theme.palette.neutral8.main,
}));

interface IProps {
  data: { coveoTrialObj: any; fieldsData: any };
  style?: any;
  [key: string]: any;
  modalProps?: any;
  state?: any;
  tagProps?: ITagProps;
}

export const MODAL_VARIANT = {
  EXIT_DISCLAIMER: 'exit_disclaimer',
};

const getmModalCTAButtonByName = (buttons: any, name: string): any => {
  if (buttons?.length > 0) {
    const item = buttons.find((item: any) => item?.name === name);
    if (item) {
      return {
        buttonVariant: item?.fields?.buttonVariant?.value,
        value: item?.fields?.link?.value?.text,
        anchor: item?.fields?.link,
        name: item?.displayName,
      };
    } else {
      return null;
    }
  }
};

const TrialCardItem = (props: IProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const trialItem = props.data.coveoTrialObj;
  const fieldsData = props.data.fieldsData;
  const customStyle = props.style || {};
  const modalProps = props?.modalProps?.modalProps || props?.modalProps;
  const cancel = getmModalCTAButtonByName(modalProps?.customButtons, 'Cancel');
  const continueBtn = getmModalCTAButtonByName(modalProps?.customButtons, 'Continue');
  const { t } = useI18n();
  const [openModal, setOpenModal] = useState(false);
  const [selectedTrial, setSelectedTrial] = useState<any>(null);
  const getNearestSite = useSavedTrialsNearestSite(props?.state);
  const [updatedModalDescription, setUpdatedModalDescription] = useState({ value: '' });
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const source = siteSettings?.source?.fields?.name?.value || 'CR';
  const GOOD_MATCH_DISTANCE = props?.fields?.goodMatchDistance?.value || 25;
  const lineCount = props?.fields?.lineCount?.value || 3;

  const isGoodMatchByDistance = useCallback(
    (item: any) => {
      const nearestSite = getNearestSite(item);
      const distance = parseFloat(nearestSite as string);
      return Math.abs(distance) <= GOOD_MATCH_DISTANCE ? true : false;
    },
    [getNearestSite]
  );

  const getSetProfileData = async () => {
    const myProfileVerificationToken = sessionStorage.getItem('myProfileVerificationToken');
    if (myProfileVerificationToken) {
      try {
        const { data } = await fetchProfileDetailsForCR(myProfileVerificationToken);
        if (data) {
          dispatch(setProfileData(data));
        }
      } catch (_e) {}
    }
  };

  const handleSaveAndUnsaveTrial = async () => {
    const payload: any = {
      myProfileToken: sessionStorage.getItem('myProfileVerificationToken'),
      trialId: selectedTrial?.raw?.trialname,
      isSaved: false,
    };
    setSelectedTrial(null);
    try {
      dispatch(showLoader());
      const { data } = await updateSavedTrialsForMyProfile(payload);
      if (data) {
        await getSetProfileData();
        if (process.env.NEXT_PUBLIC_ALLOW_REGISTRATION_API_CALL === 'true') {
          await updateSavedTrialsofCRRegUser(payload);
        }
      }
      dispatch(hideLoader());
    } catch (err) {
      dispatch(hideLoader());
    }
  };

  const onCloseModal = () => {
    setOpenModal(false);
    setSelectedTrial(null);
  };
  const onCancelModal = () => {
    setOpenModal(false);
    setSelectedTrial(null);
  };

  const handleUnsaveTrialModal = () => {
    const updatedText = mergeTag(modalProps?.description?.value, {
      TrialStudyName: trialItem?.title,
    });
    setUpdatedModalDescription({ value: updatedText });
    setOpenModal(true);
    setSelectedTrial(trialItem);
  };

  const onContinueModal = async () => {
    setOpenModal(false);
    await handleSaveAndUnsaveTrial();
  };

  const onCardLinkClick = (item: Result) => () => {
    const interactiveResultController = buildInteractiveResult(headlessEngine, {
      options: { result: item },
    });
    interactiveResultController.select();
  };
  const getRelativeHref = (fullUrl: string): string => {
    if (typeof window === 'undefined') return fullUrl; // SSR-safe fallback

    try {
      const { origin } = window.location;
      return fullUrl?.startsWith(origin) ? fullUrl?.replace(origin, '') : fullUrl;
    } catch {
      return '#';
    }
  };

  return (
    <Box
      sx={{
        padding: '24px',
        borderRadius: '16px',
        background: '#FFF',
        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
        transform: 'translateZ(0)',
        ...customStyle,
      }}
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'space-between'}
    >
      <Grid
        item
        container
        sx={{
          flexDirection: 'row',
          marginBottom: '24px',
        }}
      >
        {getStatusTags(
          trialItem,
          isGoodMatchByDistance(trialItem),
          t('goodMatch'),
          t('virtual'),
          t('enrolling'),
          t('noYetEnrolling'),
          props?.tagProps,
          '#595959'
        ).map((tag, index) => {
          return (
            <Tag
              color={tag?.color}
              index={index}
              tag={tag?.label}
              fontSize={props?.tagProps?.fontSize?.value || '13px'}
              fontWeight={props?.tagProps?.tagFontWeight?.value || 500}
            />
          );
        })}
        <Grid item flex={1} textAlign={'right'} alignSelf={'center'}>
          <Tooltip id={trialItem?.UniqueId} title={props?.fields?.saveButtonHoverText?.value}>
            {
              <FavoriteIcon
                sx={{
                  cursor: 'pointer',
                  color: props?.tagProps?.favoriteButtonColor?.value || 'black',
                }}
                onClick={() => handleUnsaveTrialModal()}
              />
            }
          </Tooltip>
        </Grid>
      </Grid>
      <Grid
        item
        sx={{
          color: '#444',
          fontFamily: `${
            siteSettings?.fontFamily?.fields?.primaryFontFamilyType?.value ||
            DEFAULT_PRIMARY_FONT_FAMILY
          }, sans-serif`,
          fontStyle: 'normal',
          fontWeight: `${source !== 'CR' ? '400' : '500'}`,
          marginBottom: '24px',
        }}
      >
        <UiText
          trimRowCount={lineCount}
          color="default"
          styles={{
            fontSize: '22px',
          }}
        >
          <StDescription dangerouslySetInnerHTML={{ __html: highlightExcerpt(trialItem) }} />
        </UiText>
      </Grid>
      <Box>
        {fieldsData.map((field: any, index: number) => (
          <Grid
            key={index}
            item
            container
            flexDirection={'row'}
            justifyContent={'space-between'}
            mb={3}
          >
            <NextImage field={field.icon} />
            <Grid
              item
              ml={2}
              fontWeight={props?.tagProps?.fontWeight?.value || '400'}
              fontSize={props?.tagProps?.fontSize?.value || '16px'}
              sx={{
                color: (theme: any) =>
                  (theme as any)?.bodyColor || (theme.palette as any).themeColor.main,
              }}
            >
              {field.label.value}
            </Grid>
            <Grid
              item
              flex={1}
              textAlign={'right'}
              sx={{
                color: (theme: any) =>
                  props?.tagProps?.valueFontColor?.value || (theme.palette as any).themeColor.main,
              }}
            >
              {field?.key?.value === 'Nearest Site'
                ? getNearestSite(trialItem) || <span>&#8212;</span>
                : field?.key?.value === 'Age'
                  ? getAgeRange(trialItem?.raw?.trialminage, trialItem?.raw?.trialmaxage) || (
                      <span>&#8212;</span>
                    )
                  : trialItem?.raw?.[KeyMapper[field.key.value]]}
            </Grid>
          </Grid>
        ))}

        <Grid
          item
          container
          spacing={1.5}
          sx={{
            boxSizing: 'border-box',
          }}
        >
          {trialItem.clickUri && (
            <Grid
              item
              xs={trialItem.raw.featuredeprurl ? 6 : 12}
              lg={trialItem.raw.featuredeprurl ? 6 : 12}
            >
              <JSSLink
                sx={{
                  width: '100%',
                  minWidth: 'initial',
                  cursor: 'pointer',
                  [theme.breakpoints.down(500)]: {
                    padding: '5px',
                  },
                }}
                id={''}
                className={''}
                key={''}
                onClick={onCardLinkClick(trialItem)}
                variant="outlined"
                field={{
                  value: {
                    href: getRelativeHref(trialItem?.clickUri),
                    text: props?.fields?.viewDetailsButton?.value || 'View Details',
                  },
                }}
                aria-label={'text'}
              />
            </Grid>
          )}
          {trialItem.raw.featuredeprurl && (
            <Grid item xs={6} lg={6}>
              <JSSLink
                sx={{
                  width: '100%',
                  minWidth: 'initial',
                  cursor: 'pointer',
                  [theme.breakpoints.down(500)]: {
                    padding: '5px',
                  },
                }}
                id={''}
                className={''}
                key={''}
                onClick={onCardLinkClick(trialItem)}
                variant="contained"
                field={{
                  value: {
                    href: trialItem?.raw?.featuredeprurl || '#',
                    text: props?.fields?.seeIfYouQualifyButton?.value || 'See if you qualify',
                  },
                }}
                aria-label={'text'}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <CRModal
        open={openModal}
        handleClose={onCloseModal}
        handleCancel={onCancelModal}
        handleContinue={onContinueModal}
        title={modalProps?.title?.value}
        confirmButton={continueBtn}
        cancelButton={cancel}
      >
        <RichText field={updatedModalDescription} />
      </CRModal>
    </Box>
  );
};
export default TrialCardItem;
